import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, API_BASE, uploadImage, getImageUrl } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import AdminLoading from './AdminLoading';
import { IoCloudUploadOutline, IoClose, IoSearchOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { buildStandardAdminPriceTiers } from '../../utils/rentalPricing';

function SeoScoreBar({ value, max, warn, label }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const color = value === 0 ? 'inherit' : value > max ? 'error' : value >= warn ? 'success' : 'warning';
  return (
    <Box sx={{ mb: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color={value > max ? 'error' : value >= warn ? 'success.main' : 'warning.main'} fontWeight={600}>
          {value}/{max}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} color={color} sx={{ height: 5, borderRadius: 3 }} />
    </Box>
  );
}

function GoogleSnippetPreview({ title, description, url }) {
  const displayTitle = title || 'Product Title';
  const displayDesc = description || 'Product description will appear here in Google search results.';
  const displayUrl = url || 'www.yoursite.com › product › product-name';
  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#fff', fontFamily: 'Arial, sans-serif' }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 600, letterSpacing: 0.5 }}>
        Google Search Preview
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#202124', mb: 0.25 }}>{displayUrl}</Typography>
      <Typography sx={{ fontSize: '1.1rem', color: '#1a0dab', fontWeight: 400, lineHeight: 1.3, mb: 0.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
        {displayTitle.slice(0, 60)}{displayTitle.length > 60 ? '…' : ''}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', color: '#4d5156', lineHeight: 1.5 }}>
        {displayDesc.slice(0, 160)}{displayDesc.length > 160 ? '…' : ''}
      </Typography>
    </Box>
  );
}

const defaultForm = {
  name: '',
  code: '',
  barcode: '',
  description: '',
  category_id: '',
  images: [],
  imageUrlInput: '',
  prices: [
    { rent_days: 1, amount_paise: 0 },
    { rent_days: 3, amount_paise: 0 },
    { rent_days: 5, amount_paise: 0 },
    { rent_days: 7, amount_paise: 0 },
  ],
  security_deposit_paise: null,
  gender: '',
  chargeTax: false,
  inStock: true,
  status: 'Published',
  tags: [],
  tagInput: '',
  attributes: { Size: [], Color: [], Material: [] },
  listing_festive_vibes: false,
  listing_occasion: false,
  styling_opening: '',
  styling_occasion: '',
  styling_pairing: '',
  styling_care: '',
  seo_title: '',
  seo_description: '',
  meta_keyphrase: '',
  og_image_url: '',
  video_url: '',
};

/** Newline-separated text ⇄ storefront “Ask AI” pools (arrays of strings). */
function linesFromSnippetText(text) {
  return String(text || '').split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
}

function snippetPoolToMultiline(snips, key) {
  if (!snips || typeof snips !== 'object') return '';
  const arr = snips[key];
  if (!Array.isArray(arr)) return '';
  return arr.map((x) => String(x).trim()).filter(Boolean).join('\n');
}

/** Map DB / CSV gender (F, M, …) to admin Select values (Female, Male). */
function normalizeGenderForForm(g) {
  const s = String(g ?? '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'f' || s === 'female' || s === 'woman' || s === 'women' || s === 'womens' || s === 'ladies') return 'Female';
  if (s === 'm' || s === 'male' || s === 'man' || s === 'men' || s === 'mens' || s === 'gents') return 'Male';
  if (g === 'Female' || g === 'Male') return g;
  return '';
}

function normalizeAttrsFromApi(data) {
  const norm = (v) => {
    if (v == null) return [];
    if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
    const t = String(v).trim();
    return t ? [t] : [];
  };
  const a = data.attributes && typeof data.attributes === 'object' ? data.attributes : {};
  let size = norm(a.Size);
  const sizesTop = Array.isArray(data.sizes) ? data.sizes.map((x) => String(x).trim()).filter(Boolean) : [];
  if (size.length === 0 && sizesTop.length) size = sizesTop;
  return {
    Size: size,
    Color: norm(a.Color),
    Material: norm(a.Material),
  };
}

function ProductImageCard({ images, onAddUrl, onRemove, showUrlInput = true }) {
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const urlFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleAdd = () => {
    const u = urlInput.trim();
    if (!u) return;
    // Prevent adding a URL that's already in the list
    if (images.includes(u)) {
      setUploadError('This image URL is already added.');
      return;
    }
    onAddUrl(u);
    setUrlInput('');
    setUploadError('');
  };
  const focusUrlField = () => {
    urlFieldRef.current?.focus?.();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (mountedRef.current) setUploadError('');
    if (mountedRef.current) setUploading(true);
    try {
      const url = await uploadImage(file);
      if (mountedRef.current) onAddUrl(url);
    } catch (ex) {
      if (mountedRef.current) setUploadError(ex.message || 'Upload failed');
    } finally {
      if (mountedRef.current) setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    if (mountedRef.current) setUploadError('');
    if (mountedRef.current) setUploading(true);
    uploadImage(file)
      .then((url) => { if (mountedRef.current) onAddUrl(url); })
      .catch((ex) => { if (mountedRef.current) setUploadError(ex.message || 'Upload failed'); })
      .finally(() => { if (mountedRef.current) setUploading(false); });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Product Image</Typography>
          <Button size="small" color="primary" onClick={focusUrlField} sx={{ textTransform: 'none' }}>
            Add media from URL
          </Button>
        </Box>
        {showUrlInput && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <TextField
              inputRef={urlFieldRef}
              size="small"
              fullWidth
              placeholder="Paste image URL"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
            />
            <Button variant="outlined" size="small" onClick={handleAdd}>Add</Button>
          </Box>
        )}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            py: 4,
            px: 2,
            textAlign: 'center',
            bgcolor: 'action.hover',
            opacity: uploading ? 0.7 : 1,
          }}
        >
          <IoCloudUploadOutline size={40} style={{ color: '#9e9e9e', marginBottom: 8 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Drag and drop an image here, or choose a file
          </Typography>
          <Button
            component="label"
            size="small"
            variant="outlined"
            sx={{ mt: 1, textTransform: 'none' }}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Browse image'}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              hidden
              onChange={handleFileSelected}
            />
          </Button>
          {uploadError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              {uploadError}
            </Typography>
          )}
        </Box>
        {images.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {images.map((url, i) => (
              <Box
                key={i}
                sx={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={getImageUrl(url)}
                  alt={`Product image ${i + 1}`}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <Box
                  sx={{
                    display: 'none',
                    position: 'absolute', inset: 0,
                    bgcolor: 'grey.100',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: 'text.secondary', p: 0.5, textAlign: 'center'
                  }}
                >
                  {url.length > 20 ? url.slice(-20) : url}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => onRemove(i)}
                  aria-label={`Remove image ${i + 1}`}
                  sx={{
                    position: 'absolute', top: 2, right: 2,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    p: '2px',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  <IoClose size={14} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/** Include selected values from the product so MUI Select shows them even if not in global variation lists (CSV imports). */
function mergeVariationOptions(apiOpts, selected) {
  const sel = selected || { Size: [], Color: [], Material: [] };
  const merge = (key) => {
    const a = Array.isArray(apiOpts[key]) ? apiOpts[key] : [];
    const s = Array.isArray(sel[key]) ? sel[key] : [];
    return Array.from(new Set([...a, ...s].map((x) => String(x).trim()).filter(Boolean)));
  };
  return {
    Size: merge('Size'),
    Color: merge('Color'),
    Material: merge('Material'),
  };
}

function VariationsCard({ attributes, onAttributesChange }) {
  const [options, setOptions] = useState({ Size: [], Color: [], Material: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/variations`)
      .then((r) => r.json())
      .then((data) => {
        if (data && typeof data === 'object') {
          setOptions({
            Size: Array.isArray(data.Size) ? data.Size : [],
            Color: Array.isArray(data.Color) ? data.Color : [],
            Material: Array.isArray(data.Material) ? data.Material : [],
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const attrs = attributes || { Size: [], Color: [], Material: [] };
  const mergedOpts = mergeVariationOptions(options, attrs);
  const update = (key, value) => {
    onAttributesChange({ ...attrs, [key]: value });
  };

  return (
    <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Variations</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which Size, Color, and Material options apply to this product. Options are managed in <Link to="/admin/variations">Variations</Link>.
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading options…</Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Size</InputLabel>
                <Select
                  multiple
                  label="Size"
                  value={attrs.Size || []}
                  onChange={(e) => update('Size', e.target.value)}
                  renderValue={(selected) => selected.join(', ') || '—'}
                >
                  {mergedOpts.Size.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Color</InputLabel>
                <Select
                  multiple
                  label="Color"
                  value={attrs.Color || []}
                  onChange={(e) => update('Color', e.target.value)}
                  renderValue={(selected) => selected.join(', ') || '—'}
                >
                  {mergedOpts.Color.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Material</InputLabel>
                <Select
                  multiple
                  label="Material"
                  value={attrs.Material || []}
                  onChange={(e) => update('Material', e.target.value)}
                  renderValue={(selected) => selected.join(', ') || '—'}
                >
                  {mergedOpts.Material.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}


export default function AdminProductAdd() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ ...defaultForm });

  useEffect(() => {
    api('/api/admin/categories').then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api(`/api/admin/products/${id}`)
      .then((data) => {
        const draft = data.is_visible === 0 || data.is_visible === false;
        const catId = data.category_id;
        setForm((prev) => ({
          ...prev,
          name: data.name || '',
          code: data.code || '',
          barcode: data.barcode || '',
          description: data.description || '',
          category_id: catId != null && catId !== '' ? String(catId) : '',
          status: draft ? 'Draft' : 'Published',
          images: (data.images && data.images.length) ? data.images.map((i) => i.url) : [],
          prices: buildStandardAdminPriceTiers(data.prices),
          security_deposit_paise: data.security_deposit_paise ?? null,
          gender: normalizeGenderForForm(data.gender),
          attributes: normalizeAttrsFromApi(data),
          listing_festive_vibes: Number(data.listing_festive_vibes) === 1,
          listing_occasion: Number(data.listing_occasion) === 1,
          styling_opening: snippetPoolToMultiline(data.styling_snippets, 'opening'),
          styling_occasion: snippetPoolToMultiline(data.styling_snippets, 'occasion'),
          styling_pairing: snippetPoolToMultiline(data.styling_snippets, 'pairing'),
          styling_care: snippetPoolToMultiline(data.styling_snippets, 'care'),
          seo_title: data.seo_title || data.meta_title || '',
          seo_description: data.seo_description || '',
          meta_keyphrase: data.meta_keyphrase || '',
          og_image_url: data.og_image_url || '',
          video_url: data.video_url || '',
          tags: Array.isArray(data.tags) ? data.tags : [],
        }));
      })
      .catch((e) => setError(e.message || 'Product not found'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleBack = () => navigate('/admin/products');

  const handleDeleteProduct = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    setDeleting(true);
    setError('');
    try {
      await api(`/api/admin/products/${id}`, { method: 'DELETE' });
      navigate('/admin/products');
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };
  const addImageUrl = (url) => setForm((f) => ({ ...f, images: [...f.images, url.trim()] }));
  const removeImage = (index) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  const addPriceRow = () => setForm((f) => ({ ...f, prices: [...f.prices, { rent_days: 1, amount_paise: 0 }] }));
  const updatePrice = (index, field, value) => setForm((f) => ({
    ...f,
    prices: f.prices.map((p, i) => i === index ? { ...p, [field]: value } : p),
  }));
  const removePrice = (index) => setForm((f) => ({ ...f, prices: f.prices.filter((_, i) => i !== index) }));

  const handlePublish = async () => {
    setError('');
    if (!form.name.trim() || !form.code.trim()) {
      setError('Name and SKU are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        barcode: form.barcode?.trim() || null,
        color: (form.attributes?.Color && form.attributes.Color[0]) || null,
        description: form.description.trim() || null,
        category_id: form.category_id ? Number(form.category_id) : null,
        images: form.images.map((u) => (typeof u === 'string' ? u : u.url)).filter(Boolean),
        prices: form.prices
          .filter((p) => p.rent_days && p.amount_paise > 0)
          .map((p) => ({ rent_days: Number(p.rent_days), amount_paise: Number(p.amount_paise) })),
        security_deposit_paise: form.security_deposit_paise != null && form.security_deposit_paise !== '' ? Number(form.security_deposit_paise) : null,
        gender: form.gender && form.gender.trim() ? form.gender.trim() : null,
        attributes_json: form.attributes || { Size: [], Color: [], Material: [] },
        sizes_json: (form.attributes?.Size && form.attributes.Size.length) ? form.attributes.Size : null,
        /** Draft = hidden from shop (maps to DB column `is_visible`); distinct from rental `status` (available/damaged). */
        is_visible: form.status === 'Published' ? 1 : 0,
        listing_festive_vibes: form.listing_festive_vibes ? 1 : 0,
        listing_occasion: form.listing_occasion ? 1 : 0,
        styling_snippets: {
          opening: linesFromSnippetText(form.styling_opening),
          occasion: linesFromSnippetText(form.styling_occasion),
          pairing: linesFromSnippetText(form.styling_pairing),
          care: linesFromSnippetText(form.styling_care),
        },
        seo_title: form.seo_title?.trim() || null,
        meta_title: form.seo_title?.trim() || null,
        seo_description: form.seo_description?.trim() || null,
        meta_keyphrase: form.meta_keyphrase?.trim() || null,
        og_image_url: form.og_image_url?.trim() || null,
        video_url: form.video_url?.trim() || null,
        tags: form.tags || [],
      };
      if (isEdit) {
        await api(`/api/admin/products/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/api/admin/products', { method: 'POST', body: JSON.stringify(payload) });
      }
      navigate('/admin/products');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {isEdit ? 'Edit Product' : 'Add a new Product'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isEdit ? 'Update product details, images, and pricing.' : 'Fill in details to add a new rental product to your catalog.'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="outlined" color="inherit" onClick={handleBack} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Button variant="contained" color="primary" onClick={handlePublish} disabled={saving || deleting} sx={{ textTransform: 'none' }}>
          {isEdit ? 'Update product' : 'Publish product'}
        </Button>
        {isEdit && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteProduct}
            disabled={saving || deleting}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? 'Deleting…' : 'Delete product'}
          </Button>
        )}
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Product information</Typography>
              <TextField
                fullWidth
                label="Name"
                placeholder="Product title"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="SKU"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Barcode"
                placeholder="0123-4567"
                value={form.barcode}
                onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description (Optional)"
                placeholder="Product Description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                multiline
                rows={4}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Ask AI — style tip pools</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Each field is optional. Enter one line per idea; the shop picks random combinations per visitor. Same button label “Ask AI” — these are scripted tips only.
              </Typography>
              <TextField
                fullWidth
                label="Opening / vibe lines"
                value={form.styling_opening}
                onChange={(e) => setForm((f) => ({ ...f, styling_opening: e.target.value }))}
                multiline
                minRows={3}
                sx={{ mb: 2 }}
                placeholder={'e.g. A soft glam moment in georgette…'}
              />
              <TextField
                fullWidth
                label="Occasion lines"
                value={form.styling_occasion}
                onChange={(e) => setForm((f) => ({ ...f, styling_occasion: e.target.value }))}
                multiline
                minRows={3}
                sx={{ mb: 2 }}
                placeholder={'e.g. Mehendi, roka cocktail, farewell…'}
              />
              <TextField
                fullWidth
                label="Pairing hints"
                value={form.styling_pairing}
                onChange={(e) => setForm((f) => ({ ...f, styling_pairing: e.target.value }))}
                multiline
                minRows={3}
                sx={{ mb: 2 }}
                placeholder={'e.g. Rose-gold studs, kolhapuris, dewy makeup…'}
              />
              <TextField
                fullWidth
                label="Wear / rental care"
                value={form.styling_care}
                onChange={(e) => setForm((f) => ({ ...f, styling_care: e.target.value }))}
                multiline
                minRows={2}
                placeholder={'e.g. Avoid perfume on zari edges; steam lightly before wear…'}
              />
            </CardContent>
          </Card>

          <ProductImageCard
            images={form.images}
            onAddUrl={addImageUrl}
            onRemove={removeImage}
            showUrlInput
          />

          <VariationsCard
            attributes={form.attributes}
            onAttributesChange={(a) => setForm((f) => ({ ...f, attributes: a }))}
          />

        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Pricing</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Standard tiers are 1, 3, 5, and 7 days. If the sheet only had a 1‑day rent, 3/5/7 are calculated the same way as the shop (linear from that rate unless you set tiers manually). “Custom” on the product page uses the same pricing rules for any date range.
              </Typography>
              {form.prices.map((p, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Days"
                    value={p.rent_days}
                    onChange={(e) => updatePrice(i, 'rent_days', e.target.value)}
                    sx={{ width: 90 }}
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Price (₹)"
                    value={p.amount_paise / 100}
                    onChange={(e) => updatePrice(i, 'amount_paise', Math.round(parseFloat(e.target.value || 0) * 100))}
                    sx={{ width: 100 }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  {form.prices.length > 1 && (
                    <IconButton size="small" onClick={() => removePrice(i)}><IoClose /></IconButton>
                  )}
                </Box>
              ))}
              <Button size="small" onClick={addPriceRow} sx={{ mt: 1, textTransform: 'none' }}>+ Add price tier</Button>
              <TextField
                size="small"
                type="number"
                label="Security deposit (₹)"
                value={form.security_deposit_paise != null && form.security_deposit_paise !== '' ? form.security_deposit_paise / 100 : ''}
                onChange={(e) => setForm((f) => ({ ...f, security_deposit_paise: e.target.value === '' ? null : Math.round(parseFloat(e.target.value || 0) * 100) }))}
                sx={{ width: 160, mt: 2, display: 'block' }}
                inputProps={{ min: 0, step: 1 }}
                helperText="Refundable deposit (INR)"
              />
              <FormControlLabel
                control={<Checkbox checked={form.chargeTax} onChange={(e) => setForm((f) => ({ ...f, chargeTax: e.target.checked }))} />}
                label="Charge tax on this product"
                sx={{ display: 'block', mt: 2 }}
              />
              <FormControlLabel
                control={<Switch checked={form.inStock} onChange={(e) => setForm((f) => ({ ...f, inStock: e.target.checked }))} />}
                label="In stock"
                sx={{ display: 'block' }}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Organize</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Gender</InputLabel>
                <Select
                  label="Gender"
                  value={form.gender || ''}
                  onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.category_id}
                  onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Shop nav collections (header: Festive vibes, Occasion)
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.listing_festive_vibes}
                    onChange={(e) => setForm((f) => ({ ...f, listing_festive_vibes: e.target.checked }))}
                    size="small"
                  />
                }
                label="Show on Festive vibes"
                sx={{ display: 'block', ml: 0, mb: 0.5 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.listing_occasion}
                    onChange={(e) => setForm((f) => ({ ...f, listing_occasion: e.target.checked }))}
                    size="small"
                  />
                }
                label="Show on Occasion"
                sx={{ display: 'block', ml: 0, mb: 2 }}
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="Published">Published</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tags (optional)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {form.tags.map((t, i) => (
                  <Chip
                    key={i}
                    label={t}
                    size="small"
                    onDelete={() => setForm((f) => ({ ...f, tags: f.tags.filter((_, j) => j !== i) }))}
                  />
                ))}
                <TextField
                  size="small"
                  placeholder="Add tag"
                  value={form.tagInput}
                  onChange={(e) => setForm((f) => ({ ...f, tagInput: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && form.tagInput.trim()) {
                      e.preventDefault();
                      setForm((f) => ({ ...f, tags: [...f.tags, f.tagInput.trim()], tagInput: '' }));
                    }
                  }}
                  sx={{ width: 100 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* SEO — full width below both columns */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: 1, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IoSearchOutline size={20} />
                <Typography variant="subtitle1" fontWeight={600}>SEO</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Optimise how this product appears in Google
                </Typography>
              </Box>

              {/* Google preview */}
              <GoogleSnippetPreview
                title={form.seo_title || form.name}
                description={form.seo_description}
                url={`www.gemplexdebug.in › product › ${form.code || 'product'}`}
              />

              <Box sx={{ mt: 2 }}>
                <SeoScoreBar value={form.seo_title.length} max={60} warn={40} label="Meta title length" />
                <SeoScoreBar value={form.seo_description.length} max={160} warn={120} label="Meta description length" />
              </Box>

              <TextField
                fullWidth
                label="SEO Title"
                placeholder="Leave blank to use product name"
                value={form.seo_title}
                onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value }))}
                sx={{ mt: 2, mb: 2 }}
                helperText={`${form.seo_title.length}/60 characters recommended`}
                inputProps={{ maxLength: 70 }}
              />

              <TextField
                fullWidth
                label="Meta Description"
                placeholder="Short summary that appears under the title in Google (120–160 chars)"
                value={form.seo_description}
                onChange={(e) => setForm((f) => ({ ...f, seo_description: e.target.value }))}
                multiline
                minRows={3}
                sx={{ mb: 2 }}
                helperText={`${form.seo_description.length}/160 characters recommended`}
                inputProps={{ maxLength: 200 }}
              />

              <TextField
                fullWidth
                label="Focus Keyword"
                placeholder="e.g. designer lehenga rental Lucknow"
                value={form.meta_keyphrase}
                onChange={(e) => setForm((f) => ({ ...f, meta_keyphrase: e.target.value }))}
                sx={{ mb: 2 }}
                helperText="Primary keyword this product page should rank for"
              />

              <TextField
                fullWidth
                label="OG Image URL (Social share image)"
                placeholder="https://… or leave blank to use first product image"
                value={form.og_image_url}
                onChange={(e) => setForm((f) => ({ ...f, og_image_url: e.target.value }))}
                helperText="Shown when this product is shared on WhatsApp, Facebook, etc."
                sx={{ mb: 1 }}
              />
              {/* Quick-pick from uploaded product images */}
              {form.images.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Pick from product images:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {form.images.map((url, i) => {
                      const resolved = getImageUrl(typeof url === 'string' ? url : url.url);
                      const rawUrl = typeof url === 'string' ? url : url.url;
                      const isSelected = form.og_image_url === rawUrl;
                      return (
                        <Box
                          key={i}
                          onClick={() => setForm((f) => ({ ...f, og_image_url: isSelected ? '' : rawUrl }))}
                          sx={{
                            width: 64, height: 64, borderRadius: 1, overflow: 'hidden', cursor: 'pointer',
                            border: isSelected ? '2px solid #1976d2' : '2px solid transparent',
                            boxShadow: isSelected ? '0 0 0 2px #90caf9' : '0 1px 3px rgba(0,0,0,0.2)',
                            transition: 'all 0.15s',
                            flexShrink: 0,
                            position: 'relative',
                          }}
                          title={isSelected ? 'Click to deselect' : 'Use as SEO image'}
                        >
                          <Box component="img" src={resolved} alt={`img ${i+1}`}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          {isSelected && (
                            <Box sx={{
                              position: 'absolute', inset: 0, bgcolor: 'rgba(25,118,210,0.18)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#1976d2', fontWeight: 700, fontSize: 18,
                            }}>✓</Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {form.og_image_url && (
                    <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                      SEO image set. <span style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setForm((f) => ({ ...f, og_image_url: '' }))}>Clear</span>
                    </Typography>
                  )}
                </Box>
              )}
              {/* Preview of current OG image */}
              {form.og_image_url && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Box component="img" src={getImageUrl(form.og_image_url)} alt="SEO preview"
                    sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {form.og_image_url}
                  </Typography>
                </Box>
              )}

              {/* Product Video */}
              <TextField
                fullWidth
                size="small"
                label="Product Video URL"
                placeholder="https://youtube.com/embed/... or https://cdn.../video.mp4"
                value={form.video_url}
                onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
                helperText="Paste a YouTube embed URL or a direct .mp4 link. Shown on the product page only if filled."
                sx={{ mb: 1, mt: 1 }}
              />
              {form.video_url && (
                <Box sx={{ mb: 2, p: 1, bgcolor: '#f3f4f6', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="success.main" fontWeight={600}>✓ Video URL set</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', flex: 1 }}>{form.video_url}</Typography>
                  <Button size="small" color="error" onClick={() => setForm((f) => ({ ...f, video_url: '' }))}>Clear</Button>
                </Box>
              )}

              {form.meta_keyphrase && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f9fbe7', borderRadius: 2, border: '1px solid #e6ee9c' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">Keyword check</Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {[
                      { label: 'In SEO title', ok: (form.seo_title || form.name).toLowerCase().includes(form.meta_keyphrase.toLowerCase()) },
                      { label: 'In meta description', ok: form.seo_description.toLowerCase().includes(form.meta_keyphrase.toLowerCase()) },
                      { label: 'In product description', ok: form.description.toLowerCase().includes(form.meta_keyphrase.toLowerCase()) },
                    ].map(({ label, ok }) => (
                      <Typography key={label} variant="caption" sx={{ color: ok ? 'success.main' : 'warning.main', fontWeight: 500 }}>
                        {ok ? '✓' : '✗'} {label}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
